import { useLayoutEffect, type RefObject } from "react";

/**
 * Lays the clear control over the slot its trigger reserves by measuring,
 * not by mirroring the trigger's padding table: the control is a sibling of
 * the trigger inside a positioned group, so `right` is the distance from the
 * group's right edge to the slot's right edge. Measured once the DOM is laid
 * out and again whenever the group or the trigger resizes (a font loading, a
 * size change, a chevron toggling). The distance is divided by the group's
 * rendered scale, so a measure taken while a dialog or drawer scales in still
 * lands on the slot once the transform settles. Nothing happens without a
 * slot.
 */
export const usePickerClearSlotPosition = ({
    clearRef,
    slotRef,
    triggerRef,
}: {
    clearRef: RefObject<HTMLButtonElement | null>;
    slotRef: RefObject<HTMLSpanElement | null>;
    triggerRef: RefObject<HTMLButtonElement | null>;
}) => {
    useLayoutEffect(() => {
        const clear = clearRef.current;
        const slot = slotRef.current;
        const trigger = triggerRef.current;
        const group = clear?.parentElement;
        if (!clear || !slot || !trigger || !group) {
            return;
        }
        const measure = () => {
            const groupRect = group.getBoundingClientRect();
            const slotRect = slot.getBoundingClientRect();
            const scale =
                group.offsetWidth > 0 ? groupRect.width / group.offsetWidth : 1;
            clear.style.right = `${(groupRect.right - slotRect.right) / scale}px`;
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(group);
        observer.observe(trigger);
        return () => {
            observer.disconnect();
        };
    }, [clearRef, slotRef, triggerRef]);
};
